import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/Components/ui/table';
import { Card, CardContent } from '@/Components/ui/card';
import { Skeleton } from '@/Components/ui/skeleton';
import { cn } from '@/lib/utils';

// Hero Stat Card Skeleton - Large prominent card
const HeroCardSkeleton = () => (
  <Card className="bg-card border border-border/40 shadow-sm">
    <CardContent className="p-6">
      <div className="flex items-start justify-between">
        <div className="flex flex-col gap-2">
          <Skeleton className="h-3 w-20" />
          <Skeleton className="h-8 w-16" />
          <Skeleton className="h-3 w-24 mt-1" />
        </div>
        <Skeleton className="w-10 h-10 rounded-lg" />
      </div>
    </CardContent>
  </Card>
);

// Pipeline Stat Card Skeleton - Smaller secondary card
const PipelineCardSkeleton = () => (
  <Card className="bg-muted/30 border border-border/30 shadow-sm">
    <CardContent className="p-4">
      <div className="flex items-center gap-3">
        <Skeleton className="w-9 h-9 rounded-lg flex-shrink-0" />
        <div className="flex flex-col gap-1 flex-1">
          <Skeleton className="h-5 w-10" />
          <Skeleton className="h-3 w-20" />
        </div>
      </div>
    </CardContent>
  </Card>
);

// Compact Card Skeleton - Smallest variant
const CompactCardSkeleton = () => (
  <div className="flex items-center gap-3 p-3 rounded-lg border border-border/20 bg-muted/20">
    <Skeleton className="w-8 h-8 rounded-md flex-shrink-0" />
    <div className="flex flex-col gap-1 flex-1">
      <Skeleton className="h-5 w-8" />
      <Skeleton className="h-3 w-16" />
    </div>
  </div>
);

// Section Header Skeleton
const SectionHeaderSkeleton = () => (
  <div className="flex items-center gap-3">
    <Skeleton className="h-4 w-20" />
    <div className="flex-1 h-px bg-border/50" />
  </div>
);

// Skeleton for Stats Cards - New 3-section layout
export const StatsCardSkeleton = () => (
  <div className="flex flex-col gap-6">
    {/* Hero Section */}
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      <HeroCardSkeleton />
      <HeroCardSkeleton />
      <HeroCardSkeleton />
      <HeroCardSkeleton />
    </div>
    
    {/* Pipeline Section */}
    <div className="flex flex-col gap-3">
      <SectionHeaderSkeleton />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <PipelineCardSkeleton />
        <PipelineCardSkeleton />
        <PipelineCardSkeleton />
        <PipelineCardSkeleton />
      </div>
    </div>
    
    {/* Collapsible Section Header (collapsed state) */}
    <div className="flex items-center gap-3">
      <Skeleton className="h-4 w-56" />
      <div className="flex-1 h-px bg-border/50" />
      <Skeleton className="w-4 h-4 rounded" />
    </div>
  </div>
);

// Skeleton for Job List
export const JobListSkeleton = () => (
  <section className="w-full py-4">
    <div className="mb-4">
      <Skeleton className="h-7 w-48" />
    </div>
    
    <div className="rounded-lg border border-border bg-card">
      <div className="overflow-hidden">
        <Table className="border-collapse">
          <TableHeader>
            <TableRow className="bg-muted/50 border-0">
              <TableHead className="w-[30%] h-12 px-4 border-0 !border-0">
                <Skeleton className="h-4 w-16" />
              </TableHead>
              <TableHead className="w-[12%] h-12 px-4 border-0 !border-0">
                <Skeleton className="h-4 w-16" />
              </TableHead>
              <TableHead className="w-[18%] h-12 px-4 border-0 !border-0">
                <Skeleton className="h-4 w-24" />
              </TableHead>
              <TableHead className="w-[25%] h-12 px-4 text-right border-0 !border-0">
                <Skeleton className="h-4 w-20 ml-auto" />
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {[1, 2, 3].map((item) => (
              <TableRow key={item} className="hover:bg-muted/30 border-0 !border-0">
                <TableCell className="py-4 px-4 border-0 !border-0">
                  <div className="flex flex-col gap-1">
                    <Skeleton className="h-5 w-32" />
                    <Skeleton className="h-4 w-40" />
                  </div>
                </TableCell>
                <TableCell className="py-4 px-4 border-0 !border-0">
                  <Skeleton className="h-6 w-16 rounded-full" />
                </TableCell>
                <TableCell className="py-4 px-4 border-0 !border-0">
                  <div className="flex items-center gap-2">
                    <Skeleton className="w-8 h-8 rounded-full" />
                    <Skeleton className="h-4 w-24" />
                  </div>
                </TableCell>
                <TableCell className="text-right py-4 px-4 border-0 !border-0">
                  <div className="flex items-center justify-end gap-2">
                    <Skeleton className="h-9 w-32 rounded-md" />
                    <Skeleton className="h-9 w-9 rounded-full" />
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  </section>
);

// Main Overview Skeleton
export const OverviewSkeleton = () => (
  <div className="flex flex-col gap-8">
    <StatsCardSkeleton />
    <JobListSkeleton />
  </div>
);

// Full Page Skeleton including sidebar
export const FullPageSkeleton = () => (
  <div className="flex flex-col gap-8">
    <StatsCardSkeleton />
    <JobListSkeleton />
  </div>
);

// Skeleton for individual job items when loading
export const JobItemSkeleton = () => (
  <div className="flex items-center gap-4 p-4 border-b border-border">
    <div className="flex-1 space-y-2">
      <Skeleton className="h-5 w-32" />
      <Skeleton className="h-4 w-40" />
    </div>
    <Skeleton className="h-6 w-16 rounded-full" />
    <Skeleton className="h-4 w-24" />
    <div className="flex gap-2">
      <Skeleton className="h-9 w-32 rounded-md" />
      <Skeleton className="h-9 w-9 rounded-full" />
    </div>
  </div>
);

// Skeleton for loading more jobs
export const LoadingMoreJobsSkeleton = () => (
  <div className="space-y-2">
    {[1, 2].map((item) => (
      <JobItemSkeleton key={`loading-${item}`} />
    ))}
  </div>
);
